// Swish 5.29's WishlistElement silently discards submit while eventPending is
// true for a variant save. Reflect that state; Swish still owns every cart write.
(() => {
  if (customElements.get('raith-wishlist-feedback')) return;
  const controls = 'button[type="submit"], select[name^="options["], input[name^="options["]';
  const option = 'select[name^="options["], input[name^="options["]';
  const contract = card => {
    const form = card?.form, variant = form?.selectedVariant;
    if (typeof card?.eventPending !== 'boolean' || typeof form?.changeOption !== 'function') return null;
    if (variant !== null && (!variant || typeof variant.available !== 'boolean' || !/^[1-9]\d*$/.test(String(variant.id)))) return null;
    return {pending: card.eventPending, variant};
  };

  class RaithWishlistFeedback extends HTMLElement {
    connectedCallback() {
      this.cleanup();
      const shop = window.Shopify, root = this.closest('.vl-content-page');
      const route = `${(shop?.routes?.root || '/').replace(/\/$/, '')}/apps/wishlist`;
      if (!root || this.dataset.shop !== 'raithclothing.myshopify.com' || !/^[1-9]\d*$/.test(this.dataset.themeId || '') ||
          shop?.shop !== this.dataset.shop || String(shop?.theme?.id) !== this.dataset.themeId || !['main', 'unpublished'].includes(shop.theme.role) ||
          location.pathname.replace(/\/$/, '') !== route ||
          !this.dataset.labelUpdating || !this.dataset.labelDelayed || !this.dataset.labelViewProduct) return;
      this.root = root;
      this.jobs = new Map();
      this.events = new AbortController();
      const signal = this.events.signal;
      root.addEventListener('change', event => {
        if (!event.target.matches?.(option)) return;
        const form = event.target.closest('form.wk-form'), card = form?.closest('wishlist-product-card');
        if (!card || !root.contains(card) || this.jobs.has(card) || !contract(card)) return;
        const job = {card, saved: new Map(), forms: new Map(), deadline: Date.now() + 20000};
        this.jobs.set(card, job);
        // Let Swish update its selected variant and Lit render its native disabled
        // states before recording them. No user event can interleave microtasks.
        queueMicrotask(() => queueMicrotask(() => this.check(job)));
      }, {capture: true, signal});
      root.addEventListener('submit', event => {
        const card = event.target.matches?.('form.wk-form') && event.target.closest('wishlist-product-card');
        const job = this.jobs.get(card);
        if (!job) return;
        if (!contract(card)?.pending) { this.finish(job); return; }
        event.preventDefault();
        event.stopImmediatePropagation();
      }, {capture: true, signal});
      // Swish's documented events use this document transport in SDK 5.29.
      // Abortable listeners also release a long wait if a late response arrives.
      for (const name of ['wk:wishlist:change-variant:success', 'wk:wishlist:change-variant:error']) {
        document.addEventListener(name, () => this.jobs.forEach(job => {
          clearTimeout(job.timer);
          job.timer = setTimeout(() => this.check(job), 0);
        }), {signal});
      }
      this.removals = new MutationObserver(records => {
        if (records.some(record => record.removedNodes.length)) {
          this.jobs.forEach(job => { if (!root.contains(job.card)) this.finish(job); });
        }
      });
      this.removals.observe(root, {childList: true, subtree: true});
      window.addEventListener('pagehide', () => this.jobs.forEach(job => this.finish(job)), {signal});
      window.addEventListener('pageshow', () => this.connectedCallback(), {signal});
    }

    check(job) {
      if (this.jobs?.get(job.card) !== job) return;
      clearTimeout(job.timer);
      const state = contract(job.card);
      if (!this.isConnected || !job.card.isConnected || !this.root.contains(job.card) || !state?.pending) {
        this.finish(job); return;
      }
      job.card.setAttribute('data-vl-wishlist-pending', '');
      for (const form of job.card.querySelectorAll('form.wk-form')) {
        if (!job.forms.has(form)) job.forms.set(form, form.getAttribute('aria-busy'));
        form.setAttribute('aria-busy', 'true');
      }
      for (const control of job.card.querySelectorAll(`form.wk-form :is(${controls})`)) {
        if (!job.saved.has(control)) job.saved.set(control, control.disabled);
        control.disabled = true;
      }
      if (!job.status) {
        job.status = document.createElement('p');
        job.status.className = 'vl-wishlist-status';
        job.status.setAttribute('role', 'status');
        job.status.setAttribute('aria-live', 'polite');
        job.status.setAttribute('aria-atomic', 'true');
        job.status.textContent = this.dataset.labelUpdating;
        job.observer = new MutationObserver(() => {
          if (!job.refreshQueued) {
            job.refreshQueued = true;
            queueMicrotask(() => { job.refreshQueued = false; this.check(job); });
          }
        });
        job.observer.observe(job.card, {childList: true, subtree: true});
      }
      if (job.status.parentElement !== job.card) job.card.append(job.status);
      if (Date.now() >= job.deadline) {
        if (!job.delayed) {
          job.delayed = true;
          job.status.textContent = this.dataset.labelDelayed;
          const source = job.card.querySelector('.wk-product-title a[href], a.wk-product-title[href], a.wk-image-link[href]');
          if (source) {
            let url;
            try { url = new URL(source.href, location.href); } catch { return; }
            if (['http:', 'https:'].includes(url.protocol) && /\/products\/[^/]+/.test(url.pathname)) {
              const link = document.createElement('a');
              link.className = 'vl-wishlist-status__link';
              link.href = source.href;
              link.textContent = this.dataset.labelViewProduct;
              job.status.append(document.createTextNode(' '), link);
            }
          }
        }
        return; // No indefinite polling and no guessed readiness or queued add.
      }
      job.timer = setTimeout(() => this.check(job), 100);
    }

    finish(job) {
      clearTimeout(job.timer);
      job.observer?.disconnect();
      const state = contract(job.card);
      for (const [control, disabled] of job.saved) {
        control.disabled = disabled || (control.matches('button[type="submit"]') && !!state && !state.variant?.available);
      }
      job.status?.remove();
      job.card.removeAttribute('data-vl-wishlist-pending');
      for (const [form, ariaBusy] of job.forms) {
        if (ariaBusy === null) form.removeAttribute('aria-busy');
        else form.setAttribute('aria-busy', ariaBusy);
      }
      this.jobs?.delete(job.card);
    }

    cleanup() {
      this.events?.abort();
      this.removals?.disconnect();
      this.jobs?.forEach(job => this.finish(job));
    }
    disconnectedCallback() { this.cleanup(); }
  }
  customElements.define('raith-wishlist-feedback', RaithWishlistFeedback);
})();

